/**
 * Générateur — Connaître la définition d'un pourcentage
 * Attendu ID : 600 | Niveau : 6ème | Domaine : NC
 *
 * Types générés : qcm_unique, texte, texte_trou_drag, texte_trou_saisie,
 *                 relier, classer, tableau_vf
 * Labels de référence :
 *   - Compléter un tableau décimal / fraction décimale / pourcentage
 *   - Relier une fraction à son pourcentage
 *   - Relier un pourcentage au nombre correspondant
 *   - Convertir un décimal ou une fraction en pourcentage
 *   - Simplifier une fraction sur 100 et associer au pourcentage
 *   - Exprimer ce que représente un pourcentage d'un nombre
 * Variantes estimées : >400
 */

import {
    randomChoice, randomChoiceMultiple, randInt, shuffle,
    makeQcm, makeSaisie, makeVraiFaux, makeTexteTrou, makeRelier, makeClasser,
    filtrerDistracteurs, tex, fracTex,
} from '../../../utils';

// ─── Constantes ───
const ATTENDU_ID = 600;
const opts = (label) => ({ niveau: '6eme', matiere: 'maths', attendu: ATTENDU_ID, label });

// ─── Pools de données ───

/** Fractions "repères" avec leur pourcentage et écriture décimale */
const REPERES = [
    { frac: '1/2', num: 1, den: 2, pct: 50, dec: '0,5' },
    { frac: '1/4', num: 1, den: 4, pct: 25, dec: '0,25' },
    { frac: '3/4', num: 3, den: 4, pct: 75, dec: '0,75' },
    { frac: '1/5', num: 1, den: 5, pct: 20, dec: '0,2' },
    { frac: '2/5', num: 2, den: 5, pct: 40, dec: '0,4' },
    { frac: '3/5', num: 3, den: 5, pct: 60, dec: '0,6' },
    { frac: '4/5', num: 4, den: 5, pct: 80, dec: '0,8' },
    { frac: '1/10', num: 1, den: 10, pct: 10, dec: '0,1' },
    { frac: '3/10', num: 3, den: 10, pct: 30, dec: '0,3' },
    { frac: '7/10', num: 7, den: 10, pct: 70, dec: '0,7' },
    { frac: '9/10', num: 9, den: 10, pct: 90, dec: '0,9' },
    { frac: '1/20', num: 1, den: 20, pct: 5, dec: '0,05' },
    { frac: '1/25', num: 1, den: 25, pct: 4, dec: '0,04' },
    { frac: '1/50', num: 1, den: 50, pct: 2, dec: '0,02' },
    { frac: '1/100', num: 1, den: 100, pct: 1, dec: '0,01' },
];

// FRACS_SUR_100 : pool statique remplacé par genFracSur100Dynamique (~59 variantes).

/** Pièges pourcentage classiques */
const PIEGES_PCT = {
    '1/4': [40, 14], // confusion avec 4/10, juxtaposition
    '1/5': [15, 50], // confusion chiffre 5, confusion avec 1/2
    '3/4': [34, 30], // juxtaposition, confusion avec 3/10
    '1/2': [12, 5],  // juxtaposition, confusion 0,5 → 5%
    '1/10': [1, 100], // 10/100 → 1%, inversion
};

/** Expressions en mots pour les pourcentages repères (étendu : usuels + semi-usuels) */
const PCT_EN_MOTS = {
    10: 'un dixième',
    20: 'un cinquième',
    25: 'un quart',
    30: 'trois dixièmes',
    40: 'deux cinquièmes',
    50: 'la moitié',
    60: 'trois cinquièmes',
    70: 'sept dixièmes',
    75: 'trois quarts',
    80: 'quatre cinquièmes',
    90: 'neuf dixièmes',
    100: 'la totalité',
};

/** Formulations variées pour "X % représentent ..." */
const EXPRIMER_PCT_FORMULATIONS = [
    (pct, mot) => `${pct} % représentent {{${mot}}} de ce nombre.`,
    (pct, mot) => `${pct} % est égal à {{${mot}}} de ce nombre.`,
    (pct, mot) => `${pct} % correspond à {{${mot}}} de ce nombre.`,
    (pct, mot) => `On peut dire que ${pct} %, c'est {{${mot}}} de ce nombre.`,
];

// ─── Helpers ───
const fracKatex = (num, den) => `\\frac{${num}}{${den}}`;
const exprElement = (contenu) => ({ type: 'expression', config: { contenu } });

// Fractions en KaTeX partout où l'élève les lit (audit fractions en texte,
// 2026-09-06). `item.frac` (« 1/4 ») reste la CLÉ des pièges, jamais un
// affichage. En formule, « % » est un commentaire LaTeX : `\%` obligatoire.
const fracDe = (item) => fracTex(item.num, item.den);
const egalitePctTex = (item, pct) => tex(`\\dfrac{${item.num}}{${item.den}} = ${pct}\\,\\%`);
// Trou dans une fraction d'une ligne KaTeX globale (motif 620-622 : `\rule`
// invisibles pour la hauteur du slot, triple accolade décodée par preprocessBraces).
const fracTrous = (num, den) => `\\dfrac{{{${num}}}\\rule[-0.55em]{0pt}{0.55em}}{\\rule{0pt}{1.3em}{{${den}}}}`;

const gcd = (a, b) => b === 0 ? a : gcd(b, a % b);

/** Convertit un entier pct en écriture décimale via manipulation de chaîne (zéro FP). */
const pctToDecStr = (pct) => {
    if (pct >= 100) return String(pct / 100);
    const padded = pct.toString().padStart(2, '0');
    const trimmed = padded.replace(/0+$/, '');
    return trimmed ? `0,${trimmed}` : '0';
};

/** Génère dynamiquement un repère {frac, num, den, pct, dec} équivalent à REPERES.
 *  pct ∈ [1, 99] → fraction réduite automatique (den ∈ {2,4,5,10,20,25,50,100}). */
const genReperDynamique = () => {
    const pct = randInt(1, 99);
    const g = gcd(pct, 100);
    const num = pct / g;
    const den = 100 / g;
    return { frac: `${num}/${den}`, num, den, pct, dec: pctToDecStr(pct) };
};

/** Génère dynamiquement une entrée {num, den:100, simpNum, simpDen, pct} pour
 *  l'exercice de simplification. num ∈ [2,99] avec gcd(num,100) > 1 (simplifiable). */
const genFracSur100Dynamique = () => {
    let num;
    let guard = 0;
    do { num = randInt(2, 99); guard++; } while (gcd(num, 100) === 1 && guard < 50);
    const g = gcd(num, 100);
    return { num, den: 100, simpNum: num / g, simpDen: 100 / g, pct: num };
};

// ─── Labels ───
const L1 = 'Compléter un tableau décimal / fraction décimale / pourcentage';
const L2 = 'Relier une fraction à son pourcentage';
const L3 = 'Relier un pourcentage au nombre correspondant';
const L4 = 'Convertir un décimal ou une fraction en pourcentage';
const L5 = 'Simplifier une fraction sur 100 et associer au pourcentage';
const L6 = 'Exprimer ce que représente un pourcentage d\'un nombre';

// ═══════════════════════════════════════════════════════════
// EXERCICE 1 : Compléter un tableau 3 formes
// ═══════════════════════════════════════════════════════════

/** Texte à trous drag — tableau décimal / fraction / pourcentage */
const genTableau3FormesDrag = () => {
    // Choisir 3 repères avec des pourcentages en centièmes (a/100)
    const items = randomChoiceMultiple(
        REPERES.filter(r => r.pct <= 100),
        3,
    );

    // Pour chaque item, cacher 1 ou 2 valeurs aléatoirement
    const trous = [];
    const distracteurs = new Set();

    items.forEach((item) => {
        // Cacher le décimal. Fraction en style TEXTE (`\frac`, pas `\dfrac`) :
        // trois lignes serrées dans un même paragraphe, la grande fraction
        // débordait sur la ligne voisine (retour écran 2026-09-06).
        trous.push(`Décimal : {{${item.dec}}} | Fraction : ${tex(`\\frac{${item.num}}{${item.den}}`)} | Pourcentage : ${item.pct} %`);
        // Ajouter un distracteur de rang
        const fakeDecimal = item.dec.includes(',')
            ? item.dec.replace(',', ',0')
            : String(item.pct / 10).replace('.', ',');
        distracteurs.add(fakeDecimal);
    });

    // Filtre contre les mots attendus : le faux-décimal fabriqué pour un item
    // peut être égal au VRAI décimal d'un autre item du tableau (ex. "0,04")
    const phrase = trous.join('\n');
    return makeTexteTrou(
        phrase,
        '<p>Complète ce tableau. Chaque ligne contient un même nombre sous 3 formes différentes.</p>',
        { ...opts(L1), mode: 'drag', distracteurs: filtrerDistracteurs(phrase, [...distracteurs]).slice(0, 3) },
    );
};

/** Texte à trous saisie — tableau 3 formes */
const genTableau3FormesSaisie = () => {
    // Génération dynamique (~99 variantes) — pct quelconque dans [1,99]
    const item = genReperDynamique();

    // Cacher les 3 formes : l'élève saisit tout
    const phrase = `${fracDe(item)} = {{${item.dec}}} = {{${item.pct}}} %`;

    return makeTexteTrou(
        phrase,
        '<p>Donne l\'écriture décimale puis l\'écriture en pourcentage.</p>',
        { ...opts(L1), mode: 'saisie' },
    );
};

// ═══════════════════════════════════════════════════════════
// EXERCICE 2 : Relier fraction ↔ pourcentage
// ═══════════════════════════════════════════════════════════

/** Relier — fraction → pourcentage */
const genRelierFracPct = () => {
    const items = randomChoiceMultiple(REPERES.filter(r => r.pct <= 100), 4);
    const pairs = items.map(item => ({
        source: fracDe(item),
        target: `${item.pct} %`,
    }));

    return makeRelier(
        pairs,
        '<p>Relie chaque nombre au pourcentage auquel il est égal.</p>',
        opts(L2),
    );
};

// ═══════════════════════════════════════════════════════════
// EXERCICE 3 : Relier pourcentage → nombre correspondant
// ═══════════════════════════════════════════════════════════

/** Relier — pourcentage → fraction */
const genRelierPctNombre = () => {
    const items = randomChoiceMultiple(REPERES.filter(r => r.pct <= 100), 5);
    const pairs = items.map(item => ({
        source: `${item.pct} %`,
        target: fracDe(item),
    }));

    return makeRelier(
        pairs,
        '<p>Relie chaque pourcentage au nombre correspondant.</p>',
        opts(L3),
    );
};

// ═══════════════════════════════════════════════════════════
// EXERCICE 4 : Convertir en pourcentage
// ═══════════════════════════════════════════════════════════

/** QCM unique — "À quel pourcentage est égal... ?" */
const genConvertirPctQcm = () => {
    const item = randomChoice(REPERES.filter(r => r.pct <= 100));
    const correct = `${item.pct} %`;

    // Choisir le format de la question : fraction ou décimal
    const useFrac = Math.random() < 0.5;
    let texte;
    if (useFrac) {
        texte = `<p>À quel pourcentage est égal {{element:0}} ?</p>`;
    } else {
        texte = `<p>À quel pourcentage est égal ${item.dec} ?</p>`;
    }

    // Distracteurs (dédupliqués pour éviter collisions quand pièges identiques)
    // Helper : formate un % en utilisant la virgule française (5/10 → "0,5 %" et non "0.5 %")
    const fmtPct = (n) => `${String(n).replace('.', ',')} %`;
    const pctPieges = PIEGES_PCT[item.frac] || [item.pct * 10, item.pct / 10];
    const distracteursRaw = [
        fmtPct(pctPieges[0]),
        fmtPct(pctPieges[1]),
        fmtPct(Math.min(item.pct + randInt(5, 15), 100)),
    ];
    const distracteurs = [...new Set(distracteursRaw)].filter(d => d !== correct);
    // Compléter si moins de 3 distracteurs uniques
    while (distracteurs.length < 3) {
        const fallback = fmtPct(Math.min(item.pct + randInt(1, 40), 100));
        if (fallback !== correct && !distracteurs.includes(fallback)) distracteurs.push(fallback);
    }

    const choix = [correct, ...distracteurs.slice(0, 3)];

    const payload = makeQcm(choix, correct, texte, opts(L4));
    if (useFrac) {
        payload.elements_additionnels = {
            version: 1,
            elements: [exprElement(fracKatex(item.num, item.den))],
        };
    }
    return payload;
};

/** Saisie — "... = ...%" */
const genConvertirPctSaisie = () => {
    // Génération dynamique (~99 variantes)
    const item = genReperDynamique();
    const useFrac = Math.random() < 0.5;

    let texte;
    if (useFrac) {
        texte = `<p>Complète : {{element:0}} = ...... %</p>`;
    } else {
        texte = `<p>Complète : ${item.dec} = ...... %</p>`;
    }

    const payload = makeSaisie(
        String(item.pct),
        texte,
        { ...opts(L4), suffixe: '%' },
    );
    if (useFrac) {
        payload.elements_additionnels = {
            version: 1,
            elements: [exprElement(fracKatex(item.num, item.den))],
        };
    }
    return payload;
};

/** Tableau V/F — "1/4 = 25%" vrai ou faux */
const genConvertirPctVF = () => {
    const items = randomChoiceMultiple(REPERES.filter(r => r.pct <= 100), 5);
    // Garantir au moins 1 V et 1 F sur 5 items (auditGen exige mix V/F)
    const nbVrai = randInt(1, 4);
    const truths = [
        ...Array(nbVrai).fill(0),
        ...Array(5 - nbVrai).fill(1),
    ].sort(() => Math.random() - 0.5);
    const lignes = [];
    const reponses = [];

    items.forEach((item, i) => {
        const isVrai = truths[i] === 0;
        if (isVrai) {
            lignes.push(egalitePctTex(item, item.pct));
            reponses.push(0);
        } else {
            const pieges = PIEGES_PCT[item.frac];
            const faux = pieges ? pieges[0] : item.pct + randInt(5, 20);
            lignes.push(egalitePctTex(item, Math.min(faux, 100)));
            reponses.push(1);
        }
    });

    return makeVraiFaux(
        lignes,
        reponses,
        '<p>Indique si chaque égalité est vraie ou fausse.</p>{{element:0}}',
        opts(L4),
    );
};

// ═══════════════════════════════════════════════════════════
// EXERCICE 5 : Simplifier une fraction sur 100
// ═══════════════════════════════════════════════════════════

/** Texte à trous saisie — "25/100 = .../..." */
const genSimplifierSaisie = () => {
    // Génération dynamique (~59 variantes) — num ∈ [2,99] avec gcd(num,100) > 1
    const item = genFracSur100Dynamique();
    // On prolonge par le trou de POURCENTAGE (num/100 = num %) : l'attendu 600 porte sur
    // la définition d'un pourcentage, jamais honorée quand la phrase s'arrête à la
    // fraction simplifiée. item.pct (= num) était calculé mais jamais réinjecté.
    // Ligne KaTeX globale : la fraction simplifiée est verticale, ses deux
    // trous dans le numérateur et le dénominateur.
    const phrase = `$\\dfrac{${item.num}}{100} = ${fracTrous(item.simpNum, item.simpDen)} = {{${item.pct}}}\\,\\%$`;

    return makeTexteTrou(
        phrase,
        // « irréductible » (et non « simplifie ») garantit l'unicité : 96/100 = 48/50 est
        // aussi une simplification valide mais non irréductible.
        '<p>Rends cette fraction irréductible et exprime-la en pourcentage.</p>',
        { ...opts(L5), mode: 'saisie' },
    );
};

// ═══════════════════════════════════════════════════════════
// EXERCICE 6 : Exprimer ce que représente un pourcentage
// ═══════════════════════════════════════════════════════════

/** Texte à trous drag — "10% représentent ... d'un nombre" */
const genExprimerPctDrag = () => {
    // Tire 1 pourcentage parmi les 12 mots usuels + semi-usuels
    const allPcts = Object.keys(PCT_EN_MOTS).map(Number);
    const pct = randomChoice(allPcts);
    const motCorrect = PCT_EN_MOTS[pct];

    // 2 distracteurs pris dans les autres mots
    const autresMots = Object.values(PCT_EN_MOTS).filter(m => m !== motCorrect);
    const distracteurs = randomChoiceMultiple(autresMots, 2);

    // Formulation variée (4 tournures possibles)
    const formulation = randomChoice(EXPRIMER_PCT_FORMULATIONS);
    const phrase = formulation(pct, motCorrect);

    return makeTexteTrou(
        phrase,
        '<p>Pour un nombre donné, complète la phrase suivante.</p>',
        { ...opts(L6), mode: 'drag', distracteurs },
    );
};

/** Classer — nombres égaux à un même pourcentage */
const genClasserPct = () => {
    // Choisir 3 pourcentages repères
    const groupes = randomChoiceMultiple(
        [
            { pct: '10 %', items: ['0,1', fracTex(1, 10), fracTex(10, 100)] },
            { pct: '25 %', items: ['0,25', fracTex(1, 4), fracTex(25, 100)] },
            { pct: '50 %', items: ['0,5', fracTex(1, 2), fracTex(5, 10)] },
            { pct: '20 %', items: ['0,2', fracTex(1, 5), fracTex(20, 100)] },
            { pct: '75 %', items: ['0,75', fracTex(3, 4), fracTex(75, 100)] },
        ],
        3,
    );

    const categories = groupes.map(g => ({
        name: g.pct,
        items: randomChoiceMultiple(g.items, 2), // 2 items par catégorie
    }));

    return makeClasser(
        categories,
        '<p>Classe ces nombres selon le pourcentage auquel ils sont égaux.</p>',
        opts(L4),
    );
};

// ─── Registre ───
const GENERATORS = {
    tableau3formes_texte_trou_drag: genTableau3FormesDrag,
    tableau3formes_texte_trou_saisie: genTableau3FormesSaisie,
    relier_frac_pct_relier: genRelierFracPct,
    relier_pct_nombre_relier: genRelierPctNombre,
    convertir_pct_qcm_unique: genConvertirPctQcm,
    convertir_pct_texte: genConvertirPctSaisie,
    convertir_pct_tableau_vf: genConvertirPctVF,
    simplifier_texte_trou_saisie: genSimplifierSaisie,
    exprimer_pct_texte_trou_drag: genExprimerPctDrag,
    classer_pct_classer: genClasserPct,
};

// ─── Catégories (obligatoire) ───
const FORMAT_LABELS = {
    qcm_unique: 'QCM', qcm_multiple: 'QCM Multi', texte: 'Saisie',
    texte_trou_drag: 'Drag', texte_trou_saisie: 'Saisie trous',
    relier: 'Relier', tableau_vf: 'Vrai/Faux', classer: 'Classer', ordonner: 'Ordonner',
};

const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'tableau3formes', label: L1, formatsList: buildFormats('tableau3formes') },
    { id: 'relier_frac_pct', label: L2, formatsList: buildFormats('relier_frac_pct') },
    { id: 'relier_pct_nombre', label: L3, formatsList: buildFormats('relier_pct_nombre') },
    { id: 'convertir_pct', label: L4, formatsList: buildFormats('convertir_pct') },
    { id: 'simplifier', label: L5, formatsList: buildFormats('simplifier') },
    { id: 'exprimer_pct', label: L6, formatsList: buildFormats('exprimer_pct') },
    { id: 'classer_pct', label: L4, formatsList: buildFormats('classer_pct') },
];

// ─── Point d'entrée (obligatoire) ───
export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
